// Create instances of Firebase services in one location
// then export as context to be used where needed

// Can add common Firebase actions to the class so they
// can be reused in multiple locations

import React from "react";
import firebase from "gatsby-plugin-firebase";
import { FEATURED_JOB_SET } from "../utl/constants";

export const FirebaseContext = React.createContext(undefined);

class Firebase {
  constructor() {
    this.static = firebase;
    this.analytics = firebase.analytics();
    this.auth = firebase.auth();
    this.db = firebase.firestore();
    this.storage = firebase.storage();
    this.func = firebase.functions();
    this.perf = firebase.performance();
    this.remoteConfig = firebase.remoteConfig();

    // Flag to indicate if remote config is fetched
    // Persists between pages because it's in FirebaseContext
    this.isFetched = null;

    //Only run emulator code if 'window' is available (Gatsby-ism)
    // if (typeof window !== undefined) {
    //   // Use the emulator when testing locally
    //   if (window.location.hostname === "localhost") {
    //     this.db.useEmulator("localhost", 8080);
    //     this.func.useEmulator("localhost", 5001);
    //     this.storage.useEmulator("localhost", 9199);
    //   }
    // }

    // Only modify remote config in a browser environment
    if (typeof window !== undefined) {
      this.remoteConfig.settings.minimumFetchIntervalMillis = 3600000;
      this.remoteConfig.defaultConfig = {
        featuredJobSet: FEATURED_JOB_SET,
      };
    }
  }

  // --- FIRESTORE FUNCTIONS ---

  getAllReferrals = async (uid) => {
    try {
      const querySnapshot = await this.db
        .collection("referrals")
        .where("referrerUid", "==", uid)
        .get();
      if (querySnapshot.empty) {
        // User has not made any referrals yet
        return [];
      } else {
        // Send back an array of docs for the getter to enumerate
        return querySnapshot.docs;
      }
    } catch (error) {
      const errorCode = error.code;
      const errorMessage = error.message;

      console.log(errorCode, errorMessage);
    }
  };

  getReferral = async (docId) => {
    try {
      const snapshot = await this.db.collection("referrals").doc(docId).get();
      if (snapshot.exists) {
        // Send back an object with the referral content
        return snapshot.data();
      } else {
        throw new Error(`Couldn't find referral with document ID: ${docId}.`);
      }
    } catch (error) {
      // Send the error to the component
      return error;
    }
  };

  getReferralByEmail = async (email) => {
    // Format email to match database just in case
    const formattedEmail = email.replace(/\s+/g, "").toUpperCase();
    try {
      const referralQuerySnapshot = await this.db
        .collection("referrals")
        .where("formattedEmail", "==", formattedEmail)
        .get();
      if (!referralQuerySnapshot.empty) {
        // Send back the referral documents
        return referralQuerySnapshot.docs;
      } else {
        // Return null so we know this member didn't have a referral
        return null;
      }
    } catch (error) {
      const errorCode = error.code;
      const errorMessage = error.message;

      console.log(errorCode, errorMessage);
    }
  };

  setReferral = async (docId, data) => {
    try {
      await this.db.collection("referrals").doc(docId).update(data);
    } catch (error) {
      const errorCode = error.code;
      const errorMessage = error.message;

      this.analytics.logEvent("setReferral", {
        errorCode: errorCode,
        errorMsg: errorMessage,
      });
    }
  };

  addHits = async (collection, hits) => {
    if (hits.length === 0) {
      // This shouldn't occur, but being defensive just in case
      return;
    }

    try {
      let formattedHit;
      for (const hit of hits) {
        // Apply a common format to reduce duplicates
        formattedHit = hit[0].toUpperCase() + hit.slice(1);
        const querySnapshot = await this.db
          .collection(collection)
          .where("value", "==", formattedHit)
          .limit(1)
          .get();
        if (querySnapshot.empty) {
          // Add the hit to the database
          this.db.collection(collection).add({ value: formattedHit });
        }
      }
    } catch (error) {
      this.analytics.logEvent("add_hit", {
        collection: collection,
        errorCode: error.code,
        errorMsg: error.message,
      });
    }
  };

  // --- REMOTE CONFIG FUNCTIONS ---
  setIsFetched = (status) => {
    this.isFetched = status;
  };
}

const Provider = (props) => {
  return (
    <FirebaseContext.Provider value={new Firebase()}>
      {props.children}
    </FirebaseContext.Provider>
  );
};

export default Provider;
